import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { create } from 'zustand';
import type { TabOption } from '../components/ui/TabSettingsButton';

/**
 * Config de réglage d'onglets publiée par la page courante.
 * Permet à l'AppHeader (global) d'afficher l'icône de réglage
 * uniquement sur les pages à onglets, sans coupler le header à
 * chaque vue. État éphémère (non persisté).
 */
export interface PageTabSettings {
  viewId: string;
  tabs: TabOption[];
  defaultHidden?: string[];
  title?: string;
  description?: string;
}

interface PageSettingsStore {
  tabSettings: PageTabSettings | null;
  setTabSettings: (settings: PageTabSettings | null) => void;
  /** Actions de page (boutons) rendues dans l'AppHeader à côté du chrono. */
  pageActions: ReactNode | null;
  setPageActions: (actions: ReactNode | null) => void;
  /** Nom de la fiche ouverte (dossier, contact…), dernier maillon du fil d'Ariane. */
  pageCrumb: string | null;
  setPageCrumb: (label: string | null) => void;
}

export const usePageSettingsStore = create<PageSettingsStore>((set) => ({
  tabSettings: null,
  setTabSettings: (settings) => set({ tabSettings: settings }),
  pageActions: null,
  setPageActions: (actions) => set({ pageActions: actions }),
  pageCrumb: null,
  setPageCrumb: (label) => set({ pageCrumb: label }),
}));

/**
 * Hook appelé par une vue à onglets pour publier sa config de réglage
 * vers le header. Publie au montage, nettoie au démontage.
 */
export function usePublishPageTabSettings(
  viewId: string,
  tabs: TabOption[],
  options?: Omit<PageTabSettings, 'viewId' | 'tabs'>,
): void {
  const setTabSettings = usePageSettingsStore((s) => s.setTabSettings);

  // Sérialise les tabs pour une dépendance stable (évite un re-publish à
  // chaque render dû à une nouvelle référence de tableau).
  const tabsKey = JSON.stringify(tabs);
  const optionsKey = JSON.stringify(options ?? {});

  useEffect(() => {
    setTabSettings({ viewId, tabs, ...options });
    return () => setTabSettings(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewId, tabsKey, optionsKey, setTabSettings]);
}

/**
 * Hook appelé par une vue pour publier ses actions (boutons) vers l'en-tête
 * de la vue autonome (nouvel onglet). L'AppHeader ne les affiche plus. Publie au montage et republie quand `deps` change
 * (pour refléter l'état courant, ex. un bouton conditionnel), nettoie au
 * démontage. `deps` doit lister ce qui influence le rendu de `actions`.
 */
export function usePublishPageActions(actions: ReactNode, deps: unknown[]): void {
  const setPageActions = usePageSettingsStore((s) => s.setPageActions);

  useEffect(() => {
    setPageActions(actions);
    return () => setPageActions(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setPageActions, ...deps]);
}

/**
 * Hook appelé par une fiche de détail pour nommer le dernier maillon du fil
 * d'Ariane de l'AppHeader (« Dossiers › Dupont c/ Atelier Nord »).
 */
export function usePublishPageCrumb(label: string | null | undefined): void {
  const setPageCrumb = usePageSettingsStore((s) => s.setPageCrumb);

  useEffect(() => {
    setPageCrumb(label ?? null);
    return () => setPageCrumb(null);
  }, [setPageCrumb, label]);
}
